import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VIP_BY_TIER, fmtCoins } from "@/lib/vipTiers";
import VipMedallion from "./VipMedallion";
import { Button } from "@/components/ui/button";

export const VIP_CHECK_EVENT = "vip:check";
export const requestVipCheck = () => window.dispatchEvent(new CustomEvent(VIP_CHECK_EVENT));

const monthLabel = (ym) => { const [y, m] = (ym || "").split("-").map(Number); return y && m ? new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : ym; };

// One-time congratulations pop-up. Awards come only from the server (committed, verified gifts/spins);
// `seen_at` is stamped server-side so the same award never shows twice — on any device.
export default function VipAwardAnnouncer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [award, setAward] = useState(null);
  const busy = useRef(false);

  const check = useCallback(async () => {
    if (!user || busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/vip/announcements/pending");
      if (data?.award) setAward(data.award);
    } catch { /* silent */ } finally { busy.current = false; }
  }, [user]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    const onCheck = () => setTimeout(check, 400);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    window.addEventListener(VIP_CHECK_EVENT, onCheck);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener(VIP_CHECK_EVENT, onCheck); document.removeEventListener("visibilitychange", onVisible); };
  }, [check]);

  const dismiss = async (goToPage) => {
    const a = award;
    setAward(null);
    try { await api.post(`/vip/announcements/${encodeURIComponent(a.id)}/seen`); } catch { /* server keeps it pending → shows again later */ }
    if (goToPage) navigate("/vip");
  };

  if (!award) return null;
  const t = VIP_BY_TIER[award.tier] || {};
  return (
    <div data-testid="vip-award-announcer" className="fixed inset-0 z-[120] grid place-items-center p-4 bg-black/80 backdrop-blur-md vip-award-backdrop" onClick={() => dismiss(false)}>
      <div className="vip-award-card relative w-full max-w-sm border border-white/10 bg-zinc-950 p-8 text-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`absolute -inset-x-20 -top-32 h-64 bg-gradient-to-b ${t.ring} opacity-25 blur-3xl pointer-events-none`} />
        <button data-testid="vip-award-close" onClick={() => dismiss(false)} className="absolute top-3 right-3 w-9 h-9 grid place-items-center text-zinc-500 hover:text-white" aria-label="Close"><X size={18} /></button>
        <div className="vip-award-sparks pointer-events-none" aria-hidden="true">{Array.from({ length: 14 }).map((_, i) => <i key={i} style={{ "--i": i }} />)}</div>
        <div className="relative flex justify-center"><VipMedallion tier={award.tier} size="xl" className="vip-award-medallion" /></div>
        <div className="relative mt-6 text-[10px] uppercase tracking-[0.35em] text-rose-400 flex items-center justify-center gap-2"><Crown size={12} /> New badge earned</div>
        <h2 data-testid="vip-award-title" className={`relative font-display text-4xl mt-2 ${t.text}`}>{award.label}</h2>
        <div data-testid="vip-award-month" className="relative text-sm text-zinc-400 mt-1">{monthLabel(award.month)} · {fmtCoins(award.coins)} verified coins purchased</div>
        <p className="relative text-xs text-zinc-500 mt-4 leading-relaxed">Thank you for supporting MyPandaX. Your badge shows beside your name in Live and on your profile all month — tiers reset on the 1st.</p>
        <div className="relative mt-6 grid gap-2">
          <Button data-testid="vip-award-view-btn" onClick={() => dismiss(true)} className="btn-crimson rounded-none py-5">View VIP &amp; Badges</Button>
          <Button data-testid="vip-award-dismiss-btn" variant="outline" onClick={() => dismiss(false)} className="rounded-none border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">Keep supporting</Button>
        </div>
      </div>
    </div>
  );
}
